import { Cap } from '../../primitives'

/** The charts Cai can build for a fleet, kept for surfaces that draw them inline. */

/** Rolling four-week cash forecast: the line dips where settlements land. */
const CASH = [41.2, 38.6, 33.1, 36.8, 29.4, 34.7, 31.2, 39.5]
const CASH_FLOOR = 25

/** Profit per truck for the month, in hundreds. One is under water. */
const TRUCKS = [
  { name: 'Truck 1', value: 42 },
  { name: 'Truck 2', value: 36 },
  { name: 'Truck 3', value: 28 },
  { name: 'Truck 4', value: -9 },
  { name: 'Truck 5', value: 31 },
]

export function CashForecast() {
  const w = 320
  const h = 90
  const max = Math.max(...CASH)
  const min = Math.min(...CASH, CASH_FLOOR)
  const x = (i: number) => (i / (CASH.length - 1)) * w
  const y = (v: number) => h - ((v - min) / (max - min)) * h
  const line = CASH.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const lowest = CASH.indexOf(Math.min(...CASH))

  return (
    <div className="border-ink-100 flex flex-col gap-2 rounded-sm border p-3">
      <Cap>Cash forecast · next 4 weeks</Cap>
      <svg
        viewBox={`0 0 ${w} ${h + 8}`}
        className="text-brand-700 w-full"
        role="img"
        aria-label="Projected cash over the next four weeks, dipping to $29.4k in week three"
      >
        <line
          x1="0"
          y1={y(CASH_FLOOR)}
          x2={w}
          y2={y(CASH_FLOOR)}
          className="stroke-ink-100"
          strokeDasharray="3 3"
          strokeWidth="1"
        />
        <path
          d={line}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* The trough is the point worth labelling; a number on every node is noise. */}
        <circle
          cx={x(lowest)}
          cy={y(CASH[lowest])}
          r="4"
          fill="currentColor"
          stroke="white"
          strokeWidth="2"
        />
      </svg>
      <div className="text-ink-400 flex justify-between text-[10px]">
        <span>Low ${CASH[lowest]}k in week 3</span>
        <span>Floor ${CASH_FLOOR}k</span>
      </div>
    </div>
  )
}

export function ProfitPerTruck() {
  const max = Math.max(...TRUCKS.map((t) => Math.abs(t.value)))

  return (
    <div className="border-ink-100 flex flex-col gap-2 rounded-sm border p-3">
      <Cap>Profit per truck · this month</Cap>
      <div className="flex flex-col gap-1.5">
        {TRUCKS.map((truck) => {
          const down = truck.value < 0
          return (
            <div key={truck.name} className="flex items-center gap-2">
              <span className="text-ink-500 w-14 shrink-0 text-[10px]">{truck.name}</span>
              <div className="flex-1">
                <div
                  className={down ? 'bg-destructive h-2 rounded-r-sm' : 'bg-brand-700 h-2 rounded-r-sm'}
                  style={{ width: `${(Math.abs(truck.value) / max) * 100}%` }}
                />
              </div>
              <span className="text-ink-400 w-12 shrink-0 text-right text-[10px]">
                {down ? '-' : ''}${Math.abs(truck.value) * 100}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Fuel against the weeks it was burned in. */
const FUEL = [7.4, 8.1, 6.9, 8.8, 7.7, 9.2]

export function FuelSpend() {
  const max = Math.max(...FUEL)

  return (
    <div className="border-ink-100 flex flex-col gap-2 rounded-sm border p-3">
      <Cap>Fuel spend · last 6 weeks</Cap>
      <div className="flex h-[90px] items-end gap-1.5">
        {FUEL.map((week, index) => (
          <div
            key={index}
            className="bg-brand-700 flex-1 rounded-t-sm"
            style={{ height: `${(week / max) * 100}%` }}
          />
        ))}
      </div>
      <span className="text-ink-400 text-[10px]">${FUEL.reduce((a, b) => a + b, 0).toFixed(1)}k total</span>
    </div>
  )
}

/** How long each broker takes to pay, which is where a crunch starts. */
const DAYS_TO_PAY = [
  { name: 'Broker X', days: 42 },
  { name: 'Dollar General', days: 28 },
  { name: 'Broker Y', days: 21 },
  { name: 'Direct', days: 12 },
]

export function DaysToPay() {
  const max = Math.max(...DAYS_TO_PAY.map((row) => row.days))

  return (
    <div className="border-ink-100 flex flex-col gap-2 rounded-sm border p-3">
      <Cap>Days to get paid</Cap>
      <div className="flex flex-col gap-1.5">
        {DAYS_TO_PAY.map((row) => (
          <div key={row.name} className="flex items-center gap-2">
            <span className="text-ink-500 w-20 shrink-0 truncate text-[10px]">{row.name}</span>
            <div className="flex-1">
              <div
                className="bg-brand-700 h-2 rounded-r-sm"
                style={{ width: `${(row.days / max) * 100}%` }}
              />
            </div>
            <span className="text-ink-400 w-6 shrink-0 text-right text-[10px]">{row.days}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
